/**
 * Local, read-only story playback for the Case 000 animation preview.
 *
 * SCOPE — this is deliberately NOT the real game:
 * it drives the character animations through the already-authored scene graph
 * so the art direction can be judged against the real narrative (the "prototype
 * to validate the style" the vision doc asks for). It does navigation only —
 * following each choice's `next`, honouring `requiresEvidence`/`requiresFlags`/
 * `staysInScene`/`diagnosticCost` exactly like the bot's `choiceIsAvailable`,
 * and collecting evidence/flags so gated choices appear. It intentionally does
 * NOT compute an authoritative score, certainty or rank: per
 * apps/mini-app/README.md that stays server-side, in the real engine
 * (packages/game-engine), reached over HTTP. The only use of `scoreDelta` here
 * is its sign, to pick a reaction animation.
 *
 * The animation selection mirrors docs/animation/STORY_ANIMATION_MAP.md.
 */

// Single source of truth: the real case content the Telegram bot ships.
// Imported at build time (resolveJsonModule); no copy, so it cannot drift.
import rawCase from '../../../telegram-bot/content/cases/case-000-v2.json';

export type Locale = 'en-US' | 'fr-FR' | 'zh-CN';

export interface Localized {
  readonly 'en-US': string;
  readonly 'fr-FR': string;
  readonly 'zh-CN': string;
}

export interface CaseChoice {
  readonly id: string;
  readonly label: Localized;
  readonly command?: Localized;
  readonly reaction: Localized;
  readonly next: string;
  readonly evidenceGranted: readonly string[];
  readonly flagsSet: readonly string[];
  readonly scoreDelta: number;
  readonly diagnosticCost?: number;
  readonly staysInScene?: boolean;
  readonly requiresEvidence?: readonly string[];
  readonly requiresFlags?: readonly string[];
}

export interface CaseScene {
  readonly id: string;
  readonly type: 'choice' | 'hypothesis' | 'accusation' | 'ending';
  readonly title: Localized;
  readonly body: Localized;
  readonly choices: readonly CaseChoice[];
  readonly ending: 'solved' | 'partial' | 'incorrect' | null;
  readonly visual?: string;
}

export interface CaseContent {
  readonly id: string;
  readonly entryScene: string;
  readonly title: Localized;
  readonly scenes: readonly CaseScene[];
  readonly evidence: ReadonlyArray<{ readonly id: string }>;
}

export const incident = rawCase as unknown as CaseContent;

/** Scenes whose base mood is the tense countdown loop, per STORY_ANIMATION_MAP.md. */
const ALERT_SCENES = new Set(['triage-console', 'containment-choice', 'evidence-recovery']);

export type Ending = 'solved' | 'partial' | 'incorrect' | null;
export type ReactionAnimation = 'react-positive' | 'react-negative';
/** Which character is "voicing" a printed console line — null is the narrator/player. */
export type Speaker = 'patch' | 'bit' | null;

/** The looping animation that matches a scene's mood. */
export function animationForScene(scene: { id: string; ending: Ending }): string {
  if (scene.ending === 'solved') return 'solved';
  if (scene.ending === 'partial') return 'partial';
  if (scene.ending === 'incorrect') return 'incorrect';
  return ALERT_SCENES.has(scene.id) ? 'alert' : 'idle';
}

/** The one-shot reaction a choice triggers, or undefined for a neutral move. */
export function reactionForChoice(choice: CaseChoice): ReactionAnimation | undefined {
  if (choice.scoreDelta > 0) return 'react-positive';
  if (choice.scoreDelta < 0) return 'react-negative';
  return undefined;
}

export function detectLocale(): Locale {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();
  if (lang.startsWith('fr')) return 'fr-FR';
  if (lang.startsWith('zh')) return 'zh-CN';
  return 'en-US';
}

/**
 * Navigation-only playback state. Mirrors the fields the real engine tracks
 * that are needed to decide which choices are available — nothing more.
 */
export class StoryPlayback {
  private sceneId: string;
  private readonly evidence = new Set<string>();
  private readonly flags = new Set<string>();
  private readonly actionsTaken = new Set<string>();
  private diagnosticsRemaining = 3;
  private readonly sceneById: ReadonlyMap<string, CaseScene>;

  constructor() {
    this.sceneById = new Map(incident.scenes.map((scene) => [scene.id, scene]));
    this.sceneId = incident.entryScene;
  }

  get currentScene(): CaseScene {
    const scene = this.sceneById.get(this.sceneId);
    if (scene === undefined) throw new Error(`Unknown scene: ${this.sceneId}`);
    return scene;
  }

  get isEnding(): boolean {
    return this.currentScene.type === 'ending';
  }

  get evidenceCount(): number {
    return this.evidence.size;
  }

  get evidenceTotal(): number {
    return incident.evidence.length;
  }

  /** Same availability predicate as apps/telegram-bot ... bot.ts `choiceIsAvailable`. */
  availableChoices(): readonly CaseChoice[] {
    const scene = this.currentScene;
    return scene.choices.filter((choice) => {
      if (this.actionsTaken.has(`${scene.id}:${choice.id}`)) return false;
      if ((choice.requiresEvidence ?? []).some((id) => !this.evidence.has(id))) return false;
      if ((choice.requiresFlags ?? []).some((id) => !this.flags.has(id))) return false;
      return (choice.diagnosticCost ?? 0) <= this.diagnosticsRemaining;
    });
  }

  /** Apply a choice and advance the scene. Returns the chosen choice's reaction animation. */
  choose(choice: CaseChoice): string | undefined {
    const scene = this.currentScene;
    this.actionsTaken.add(`${scene.id}:${choice.id}`);
    for (const id of choice.evidenceGranted) this.evidence.add(id);
    for (const id of choice.flagsSet) this.flags.add(id);
    this.diagnosticsRemaining -= choice.diagnosticCost ?? 0;
    this.sceneId = choice.next;
    return reactionForChoice(choice);
  }

  restart(): void {
    this.evidence.clear();
    this.flags.clear();
    this.actionsTaken.clear();
    this.diagnosticsRemaining = 3;
    this.sceneId = incident.entryScene;
  }
}
